import { Component, DestroyRef, OnInit } from '@angular/core';
import { SbDateTime } from '../../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { API, FrameDTO, FramedReportingDTO } from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { ActivatedRoute } from '@angular/router';
import { QueryParamService } from '../../../../services/lists/query-params';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-page-location-statistics-visitor',
  templateUrl: './location-statistics-visitor.component.html',
  styleUrls: ['./location-statistics-visitor.component.scss'],
  standalone: false,
})
export class LocationStatisticsVisitorComponent implements OnInit {
  visitorTimeData: FrameDTO[];
  loading: boolean;
  locationId: number;
  interval: number = 86400;
  debounceTimer: ReturnType<typeof setTimeout>;

  from?: SbDateTime;
  till?: SbDateTime;
  queryParamsParsed: boolean;

  constructor(
    private readonly api: API,
    private readonly errorHandler: ErrorService,
    private readonly queryParamService: QueryParamService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly destroyRef: DestroyRef
  ) {
    this.loading = true;
    this.queryParamsParsed = false;
    this.till = new SbDateTime(DateTime.local().startOf('week').minus({ days: 1 }));
    this.from = new SbDateTime(this.till.value.minus({ days: 6 }));
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.locationId = +params.locationID;
      this.parseDateParams();
      this.requestDebounced();
    });
  }

  refreshFromServer() {
    this.loading = true;
    if (!this.from && !this.till) {
      this.from = new SbDateTime(DateTime.utc().startOf('day').minus({ day: 8 }));
      this.till = new SbDateTime(DateTime.utc().startOf('day').minus({ day: 1 }));
    }
    this.api
      .dm_visitor_VisitorTrackerReporting_trackedVisitorFramed({
        locations: [this.locationId],
        from: this.from,
        till: this.till,
        productive: true,
        interval: this.interval,
      })
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (data: FramedReportingDTO) => {
          if (data?.buckets?.length > 0) {
            this.visitorTimeData = data.buckets[0].frames;
          } else {
            this.visitorTimeData = [];
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  requestDebounced() {
    if (this.queryParamsParsed) {
      this.refreshQueryParams();
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  parseDateParams() {
    const from = this.queryParamService.getQueryParam('from');
    const till = this.queryParamService.getQueryParam('till');
    if (from && till) {
      this.from = new SbDateTime(DateTime.fromISO(from));
      this.till = new SbDateTime(DateTime.fromISO(till));
    }
    this.queryParamsParsed = true;
  }

  refreshQueryParams() {
    if (this.from) {
      this.queryParamService.setQueryParam('from', this.from.toString());
    } else {
      this.queryParamService.clearQueryParam('from');
    }
    if (this.till) {
      this.queryParamService.setQueryParam('till', this.till.toString());
    } else {
      this.queryParamService.clearQueryParam('till');
    }
  }

  handleChartControlChange($event: { interval: number; dates: { fromDate: SbDateTime; toDate: SbDateTime } }) {
    if ($event) {
      this.interval = $event.interval;
      this.from = $event.dates.fromDate;
      this.till = $event.dates.toDate;
      this.requestDebounced();
    }
  }
}
